/**
 * Bookmark persistence — dual-write to localStorage AND backend.
 *
 * The backend `/api/bookmarks` endpoints (shipped in mobile p11) are the
 * source of truth. localStorage acts as a fast cache for offline/fallback.
 *
 * On add:  write to localStorage first, then POST /api/bookmarks (fire-and-forget).
 * On delete: remove from localStorage first, then DELETE /api/bookmarks/{id} (fire-and-forget).
 * On load:  GET /api/bookmarks, merge with localStorage, update both.
 */

import { api } from "@/lib/api";

const BOOKMARKS_KEY = "travelbook_bookmarks";

/* ─── localStorage helpers ─── */

/** Read all locally stored bookmarks. */
export function getLocalBookmarks() {
  try {
    const raw = localStorage.getItem(BOOKMARKS_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch (_e) {
    return [];
  }
}

function setLocalBookmarks(arr) {
  try {
    localStorage.setItem(BOOKMARKS_KEY, JSON.stringify(arr));
  } catch (_e) { /* ignore */ }
}

/* ─── Encode helper ─── */

/**
 * Encode a card ID for use in URL paths.
 * Discover card IDs may contain colons (e.g. "cruisedirect:xyz") which must
 * be percent-encoded or the k8s ingress strips the path segment.
 */
export function encodeCardId(id) {
  return encodeURIComponent(id);
}

/* ─── Dual-write: add ─── */

/**
 * Add a card to bookmarks. Writes to localStorage immediately, then
 * fires POST /api/bookmarks in the background.
 */
export function addLocalBookmark(card) {
  // 1. localStorage (immediate, never blocks UI)
  try {
    const arr = getLocalBookmarks();
    if (!arr.some((c) => c.id === card.id)) {
      arr.unshift(card);
      setLocalBookmarks(arr);
    }
  } catch (_e) { /* ignore */ }

  // 2. Backend POST (fire-and-forget)
  const loc = card.location || {};
  const payload = {
    id: card.id,
    title: card.name || card.title || "",
    subtitle: card.subtitle || [loc.city, loc.country].filter(Boolean).join(", ") || "",
    image: card.images?.[0] || card.image || "",
    provider: card.provider || "",
    vibe: card.vibe || "",
    payload: {
      price_from: card.price_from ?? card.price_usd ?? null,
      currency: card.currency || "USD",
      booking_url: card.booking_url || card.affiliate_url || "",
      description: (card.description || "").slice(0, 500),
      metadata: card.metadata || {},
      location: card.location || {},
      price_label: card.price_label || "",
      rating: card.rating || null,
    },
  };
  api.post("/bookmarks", payload).catch((err) => {
    console.warn("[bookmarks] POST /api/bookmarks failed — local copy retained", err?.message);
  });
}

/** Remove a card from bookmarks by ID. Dual-write: localStorage + backend. */
export function removeLocalBookmark(cardId) {
  // 1. localStorage
  try {
    const arr = getLocalBookmarks().filter((c) => c.id !== cardId);
    setLocalBookmarks(arr);
  } catch (_e) { /* ignore */ }

  // 2. Backend DELETE (fire-and-forget)
  api.delete(`/bookmarks/${encodeCardId(cardId)}`).catch((err) => {
    console.warn("[bookmarks] DELETE /api/bookmarks failed", err?.message);
  });
}

/* ─── Merge: fetch backend + merge with localStorage ─── */

/**
 * Fetch bookmarks from backend AND localStorage, merge (union, dedupe by id),
 * and update localStorage with the merged set.
 * Returns the merged array of bookmark objects.
 */
export async function fetchAndMergeBookmarks() {
  const local = getLocalBookmarks();

  let backend = [];
  try {
    const { data } = await api.get("/bookmarks");
    backend = data.bookmarks || [];
  } catch (_e) {
    // Backend unavailable — return local only
    return local;
  }

  // Merge: backend items take precedence; add any local-only items
  const seen = new Set(backend.map((b) => b.id));
  const localOnly = local.filter((lb) => !seen.has(lb.id));

  // Push local-only items to backend in the background
  for (const card of localOnly) {
    addLocalBookmark(card); // re-triggers POST for items that were only local
  }

  // Build merged list: backend items enriched with local payload, then local-only items
  const merged = [
    ...backend.map((b) => enrichBackendBookmark(b, local)),
    ...localOnly,
  ];

  // Update localStorage with the full merged set
  setLocalBookmarks(merged);

  return merged;
}

/**
 * Enrich a backend bookmark record with the richer local card data
 * (if it exists) so we can render full cards.
 */
function enrichBackendBookmark(b, localArr) {
  const localMatch = localArr.find((l) => l.id === b.id);
  if (localMatch) return localMatch;

  // Convert backend BookmarkOut shape → discover-card-like shape
  // so the UI can render it uniformly.
  return {
    id: b.id,
    name: b.title || "",
    title: b.title || "",
    subtitle: b.subtitle || "",
    images: b.image ? [b.image] : [],
    image: b.image || "",
    provider: b.provider || "",
    vibe: b.vibe || "",
    price_from: b.payload?.price_from ?? null,
    price_usd: b.payload?.price_from ?? 0,
    currency: b.payload?.currency || "USD",
    booking_url: b.payload?.booking_url || "",
    affiliate_url: b.payload?.booking_url || "",
    description: b.payload?.description || "",
    metadata: b.payload?.metadata || {},
    location: b.payload?.location || {},
    price_label: b.payload?.price_label || "",
    rating: b.payload?.rating || null,
    saved_at: b.saved_at || "",
    // For the post type
    _backendBookmark: b,
  };
}

/**
 * Normalize a discover card to the legacy destination shape so the
 * Bookmarks UI doesn't need to dual-render.
 */
export function toBookmarkShape(card) {
  const loc = card.location || {};
  return {
    id: card.id,
    name: card.name || card.title || "",
    country: loc.country || loc.city || "",
    code: (loc.country || "").slice(0, 2).toUpperCase() || "",
    image: card.images?.[0] || card.image || "",
    price_usd: card.price_from ?? card.price_usd ?? 0,
    affiliate_url: card.booking_url || card.affiliate_url || "",
    duration: card.metadata?.duration_nights
      ? `${card.metadata.duration_nights} nights`
      : card.metadata?.duration?.value
        ? `${card.metadata.duration.value} ${card.metadata.duration.metric || "days"}`
        : "\u2014",
    best_season: card.vibe || "any",
    _raw: card,
  };
}
